import React from 'react';
import { Link } from 'react-router-dom';
import ContentPageLayout from './components/ContentPageLayout';

const PrivacyPage = () => {
  return (
    <ContentPageLayout
      eyebrow="TECNIQ policies"
      title="Privacy Policy"
      intro="A clear overview of how TECNIQ handles local-first training data, optional cloud services, media backup, and account features."
      lastUpdated="March 28, 2026"
      companionLink={{ to: '/terms', label: 'View Terms of Use' }}
    >
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Introduction</h2>
                    <p>
                        Tecniq is a local-first fitness tracking app. This Privacy Policy explains what information we
                        process when you use Tecniq, when data stays only on your device, when optional cloud features
                        may store data remotely, and what controls you have over that data.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Local-First Data Storage</h2>
                    <p>
                        By default, Tecniq stores your workout and app data locally on your device. If you use Tecniq
                        without signing in or without enabling supported cloud features, your data generally remains on
                        that device.
                    </p>
                    <p>Local data can include:</p>
                    <ul style={{ marginLeft: 24 }}>
                        <li>Workout logs, routines, templates, and exercise history</li>
                        <li>Body logs, fitness metrics, and progress data</li>
                        <li>App settings, preferences, and configuration</li>
                        <li>Photos, videos, thumbnails, and cached media stored on your device</li>
                    </ul>
                    <p>
                        Some optional features described below can back up or sync supported data to cloud services when
                        they are available, enabled, and used by you.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Account and Sign-In Data</h2>
                    <p>
                        If you create or use a Tecniq account, we may process account information such as your email
                        address, authentication identifiers, and related account status information to let you sign in,
                        manage your account, restore access, and support cloud-enabled features.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Cloud Sync and Backup</h2>
                    <p>
                        Tecniq may offer optional cloud sync or backup for supported app data. These features are not part
                        of the default local-only experience. They require supported app functionality and typically require
                        you to sign in. In some cases they may also depend on your subscription tier.
                    </p>
                    <p>When enabled and available, cloud-backed data may include:</p>
                    <ul style={{ marginLeft: 24 }}>
                        <li>Workout, routine, and body-log records</li>
                        <li>Selected settings and backup status information</li>
                        <li>Upload queue, sync, and restore state needed to support backup and recovery</li>
                    </ul>
                    <p>
                        If cloud sync is disabled, unavailable, or you are not signed in, supported data remains local to
                        your device unless you take another action that shares or exports it.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Media Storage and Cloud Media Backup</h2>
                    <p>
                        Tecniq includes photo and video features for workout and progress tracking. Media you capture is
                        stored locally on your device by default. If cloud media storage is available for your account and
                        you choose a cloud-capable storage mode, Tecniq may upload supported photos or videos and related
                        metadata to remote storage for backup, playback, management, and restore purposes.
                    </p>
                    <p>Cloud media information may include:</p>
                    <ul style={{ marginLeft: 24 }}>
                        <li>Media files, thumbnails, and file metadata such as type, size, checksum, and backup status</li>
                        <li>Storage usage information, upload progress, and cloud playback or download references</li>
                        <li>Locally cached or downloaded copies created for playback, editing, or offline access</li>
                    </ul>
                    <p>
                        Depending on the storage mode you choose, uploaded media may remain on your device after upload, or
                        a cloud playback/download action may create cached copies on your device.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Analytics, Diagnostics, and Feedback</h2>
                    <p>
                        Tecniq may process analytics and diagnostics information to understand app usage, improve
                        performance, troubleshoot issues, and develop features. This can include app events, screen views,
                        device and app version information, and technical performance data.
                    </p>
                    <p>
                        If analytics is enabled in the app, Tecniq may send usage and performance events to analytics
                        providers such as PostHog. Tecniq is designed to reduce direct personal information in analytics
                        events, but analytics data may still be associated with your device or session.
                    </p>
                    <p>
                        If you voluntarily send feedback or support information, we may also receive the information you
                        choose to share, such as logs, app version details, device details, or issue descriptions.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Notifications</h2>
                    <p>
                        If you enable notifications and grant permission on your device, Tecniq may process notification
                        settings, device registration details, and push notification tokens so we can deliver supported
                        notifications through services such as Expo.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Subscriptions and Payments</h2>
                    <p>
                        Tecniq may offer subscription tiers that unlock cloud-related or other premium features. To support
                        subscriptions, we may process subscription status, tier, entitlement, and offering information
                        through subscription service providers such as RevenueCat and through Apple App Store or Google Play
                        billing systems.
                    </p>
                    <p>
                        Tecniq does not state in this policy that it directly stores your full payment card details. Payment
                        processing is handled by the applicable app store and related subscription providers.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>How We Use Data</h2>
                    <p>We use information described in this policy to:</p>
                    <ul style={{ marginLeft: 24 }}>
                        <li>Provide local-first workout tracking and related app functionality</li>
                        <li>Authenticate users and manage accounts</li>
                        <li>Back up, sync, restore, store, and play back cloud-enabled data and media</li>
                        <li>Process subscriptions and feature entitlements</li>
                        <li>Send notifications you have enabled</li>
                        <li>Maintain security, troubleshoot issues, and improve the app</li>
                    </ul>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Third-Party Service Providers</h2>
                    <p>
                        Depending on the features you use, Tecniq may rely on third-party service providers that process
                        data on our behalf or as part of the services they provide, including:
                    </p>
                    <ul style={{ marginLeft: 24 }}>
                        <li>Supabase and PowerSync for account services, authentication, and cloud sync</li>
                        <li>RevenueCat and the Apple App Store or Google Play for subscriptions and entitlements</li>
                        <li>PostHog for analytics and product improvement</li>
                        <li>Expo and related notification services for push notification delivery</li>
                        <li>Cloud media storage providers used to store or deliver backed-up media</li>
                    </ul>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Data Security and Retention</h2>
                    <p>
                        We use reasonable administrative, technical, and organizational measures designed to protect data
                        processed through Tecniq. No system can be guaranteed completely secure, and the security of local
                        data also depends on the protections applied to your device.
                    </p>
                    <p>
                        We keep data for as long as needed to provide the relevant features, maintain your account, comply
                        with legal obligations, resolve disputes, and enforce our agreements. Retention can vary depending
                        on whether data is stored only locally, backed up to cloud services, or submitted for support.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>International Data Transfers</h2>
                    <p>
                        If you use cloud, analytics, subscription, notification, or support features, your information may
                        be processed in countries other than the one where you live. Those countries may have different data
                        protection laws than your jurisdiction.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Your Controls and Choices</h2>
                    <p>You can control how Tecniq handles your data in several ways:</p>
                    <ul style={{ marginLeft: 24 }}>
                        <li>Use Tecniq locally without enabling supported cloud features</li>
                        <li>Enable or disable automatic cloud backup or sync where the app supports that control</li>
                        <li>Choose local-only or cloud-capable media storage modes where available</li>
                        <li>Delete local app data, media, cached files, cloud media, or wipe local and cloud data from the app where supported</li>
                        <li>Sign out, delete your account, or contact us about deletion requests related to data we control</li>
                        <li>Adjust analytics or notification preferences where available in the app or your device settings</li>
                    </ul>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Sharing of Information</h2>
                    <p>
                        We do not sell your personal information. We may share information with service providers and
                        platform partners as needed to operate the app and its features, including cloud sync, media backup,
                        analytics, notifications, subscriptions, legal compliance, security, or business transfers.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Your Rights</h2>
                    <p>Depending on your location, you may have rights to:</p>
                    <ul style={{ marginLeft: 24 }}>
                        <li>Access, correct, delete, or request a copy of certain personal information</li>
                        <li>Withdraw consent where processing is based on consent</li>
                        <li>Object to or restrict certain processing in some circumstances</li>
                        <li>Receive information about how Tecniq handles your data</li>
                    </ul>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Changes to This Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time to reflect changes in Tecniq, our data
                        practices, or legal requirements. We may communicate significant updates through the app or website.
                    </p>
      </section>
      <section>
                    <h2 className="headline" style={{ marginBottom: 10 }}>Contact Us</h2>
                    <p>
                        If you have questions about this Privacy Policy, want to request deletion, or need to contact us
                        about privacy matters, please contact us:
                    </p>
                    <ul style={{ marginLeft: 24 }}>
                        <li>By visiting our <Link to="/#contact">contact page</Link></li>
                                                <li>
                                                    By email: <a href="mailto:main@tecniq.com.au">main@tecniq.com.au</a>
                                                </li>
                    </ul>
            </section>
        </ContentPageLayout>
    );
};

export default PrivacyPage;
